import { useEffect, useState } from 'react';
import { X, ExternalLink } from 'lucide-react';
import api from '../services/api';
import { useLanguage } from '../hooks/useLanguage';
import { fuehrtZurRegistrierung, useAnmeldeFreigabe } from '../hooks/useAnmeldeFreigabe';
import { useTranslation } from '../hooks/useTranslation';
import { istWegklickbar } from '../utils/bannerWegklickbar';
import HinweisZeile from './hinweisleiste/HinweisZeile';
import { ZEILE_CSS, stilVorlage } from './hinweisleiste/stil';

/**
 * BannerStrip — admin-konfigurierbare Hinweis-Leiste am Page-Top.
 *
 * Lädt aktive Banner (Backend filtert nach is_active + Zeitfenster) und rendert
 * sie ggf. als Stack untereinander. User-Dismiss wird in localStorage gemerkt
 * (pro Banner-ID), damit er nicht ständig zurückkommt — ausser beim
 * Beta-Hinweis: der ist nie wegklickbar (`istWegklickbar`, Fleet-Kanon
 * 17.10.2026), ein alter Eintrag versteckt ihn nicht.
 *
 * Am Telefon steht die Nachricht auf EINER Zeile (Audit P9: die Beta-Leiste
 * nahm 127 px, bevor der eigentliche Inhalt begann); „Mehr" klappt sie auf.
 * Aussehen in `hinweisleiste/stil.ts` (geteilt mit der Leiste der 404).
 *
 * Props:
 *   scope: 'login' | 'public' | 'app' — passt die Banner-Auswahl an die Seite
 *
 * Backend: GET /api/banners?scope=...
 *
 * Use-Cases (z.B. aus survey-app übernommen):
 *   - "Beta-Phase: kostenlos solange Beta läuft"
 *   - Wartungs-Ankündigung
 *   - Feature-Highlight nach Release
 */
// §5-i18n: Felder sind {locale: string}-Maps; Fallback-Kette locale→en→de
// (Legacy-Banner tragen nur de — Verhalten wie vor der Umstellung).
function localized(map, lang) {
  if (!map) return null;
  if (typeof map === 'string') return map; // defensiv: alte Cache-Antworten
  return map[lang] || map.en || map.de || null;
}

export default function BannerStrip({ scope = 'all' }) {
  const { language } = useLanguage();
  const { t } = useTranslation();
  const [banners, setBanners] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem('paperball-dismissed-banners') || '[]'));
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    let cancelled = false;
    api.get(`/api/banners?scope=${encodeURIComponent(scope)}`)
      .then(r => {
        if (cancelled) return;
        // Defensiv: falls Response kein Array ist, leeres Array nutzen statt zu crashen
        setBanners(Array.isArray(r?.data) ? r.data : []);
      })
      // Banner sind nicht-kritisch — die Seite läuft ohne sie weiter, aber
      // laut: ein fehlender Beta-Hinweis soll in der Konsole auffallen.
      .catch(err => { console.error('Hinweisleisten konnten nicht geladen werden', err); });
    return () => { cancelled = true; };
  }, [scope]);

  const handleDismiss = (id) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    try {
      localStorage.setItem('paperball-dismissed-banners', JSON.stringify([...next]));
    } catch { /* localStorage voll/disabled — egal */ }
  };

  // Weggeklickt gilt nur, solange der Banner wegklickbar ist: Stellt die
  // Verwaltung ihn auf „nicht wegklickbar" (oder ist er ein Beta-Hinweis),
  // kommt er auch bei denen zurück, die ihn früher geschlossen haben (sonst
  // blieb er dort für immer weg).
  const visible = banners.filter(b => !(istWegklickbar(b) && dismissed.has(b.id)));
  if (visible.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {visible.map(b => (
        <SingleBanner key={b.id} banner={b} lang={language || 'de'} onDismiss={handleDismiss} t={t} />
      ))}
      <style>{ZEILE_CSS}</style>
    </div>
  );
}

function SingleBanner({ banner, lang, onDismiss, t }) {
  const preset = stilVorlage(banner.style);
  // Ein Banner-Ziel kommt aus der Datenbank, nicht aus dem Quelltext — der
  // Wächter über den /register-Links im Code sieht es deshalb nicht. Zeigt es
  // auf die Registrierung, gilt derselbe Schalter wie für jeden anderen Weg
  // dorthin: In einem gesperrten Markt (oder solange die Freigabe fehlt) steht
  // hier keine Aufforderung. Der Text des Banners bleibt — er gehört dem
  // Betreiber, der Weg in die Registrierung nicht.
  const { registrierung: registrierungOffen } = useAnmeldeFreigabe();
  const title = localized(banner.title, lang);
  const message = localized(banner.message, lang);
  const ctaLabel = localized(banner.cta_label, lang);
  if (!message) return null;
  return (
    <HinweisZeile
      stil={banner.style}
      titel={title}
      nachricht={message}
      mehr={t('banner.more', 'Mehr')}
      weniger={t('banner.less', 'Weniger')}
    >
      {/* Der Knopf steht ausserhalb des gekürzten Textes — sonst wäre er
          am Telefon mitgekürzt. */}
      {banner.cta_url && ctaLabel
        && (registrierungOffen || !fuehrtZurRegistrierung(banner.cta_url)) && (
        <a
          href={banner.cta_url}
          target={banner.cta_url.startsWith('http') ? '_blank' : undefined}
          rel={banner.cta_url.startsWith('http') ? 'noopener noreferrer' : undefined}
          style={{
            color: preset.accent,
            textDecoration: 'underline',
            fontWeight: 500,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            whiteSpace: 'nowrap',
          }}
        >
          {ctaLabel}
          {banner.cta_url.startsWith('http') && <ExternalLink size={12} />}
        </a>
      )}
      {istWegklickbar(banner) && (
        <button
          type="button"
          onClick={() => onDismiss(banner.id)}
          style={{
            background: 'none',
            border: 'none',
            color: preset.text,
            cursor: 'pointer',
            padding: 4,
            opacity: 0.7,
            marginLeft: 'auto',
          }}
          aria-label={t('common.close', 'Schließen')}
        >
          <X size={16} />
        </button>
      )}
    </HinweisZeile>
  );
}
